import React, { useMemo } from 'react';
import './moodmate-visual.css';

/**
 * FloatingParticleLayer — a handful of soft particles drifting slowly upward.
 *
 * Positions/sizes/timings are computed ONCE (useMemo seeded by count) so the layer
 * never re-renders on parent updates and never churns React. Decorative and
 * aria-hidden; hidden entirely under prefers-reduced-motion and thinned on small
 * screens (see moodmate-visual.css).
 */
export const FloatingParticleLayer: React.FC<{ count?: number }> = ({ count = 12 }) => {
  const dots = useMemo(
    () =>
      Array.from({ length: count }, (_, i) => {
        // Deterministic pseudo-spread so the field looks scattered but stable.
        const r = (n: number) => ((Math.sin((i + 1) * n) + 1) / 2);
        const size = 4 + Math.round(r(12.9898) * 9);
        return {
          left: `${Math.round(r(78.233) * 100)}%`,
          size,
          duration: `${18 + Math.round(r(3.14) * 20)}s`,
          delay: `${-Math.round(r(7.77) * 20)}s`,
          drift: `${Math.round((r(5.5) - 0.5) * 60)}px`,
          opacity: 0.2 + r(2.2) * 0.3,
        };
      }),
    [count]
  );

  return (
    <div className="mm-particles" aria-hidden="true">
      {dots.map((d, i) => (
        <span
          key={i}
          className="mm-particles__dot"
          style={{
            left: d.left,
            width: d.size,
            height: d.size,
            opacity: d.opacity,
            animationDuration: d.duration,
            animationDelay: d.delay,
            ['--mm-drift' as string]: d.drift,
          }}
        />
      ))}
    </div>
  );
};

export default FloatingParticleLayer;
